import { TimelineLog, ITimelineLogSnapshotIn } from '../TimelineLog';
import { IntlShape } from 'react-intl';
import { DateTime } from '../DateTime';
import { Address } from '../Address';
import {
  flow,
  getRoot,
  IAnyModelType,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from 'mobx-state-tree';
import { RootModelType } from '../Root';
import { modelWithCRUD } from '../utils/modelWithCRUD';
import { CommomMixin, ResponsibleMixin } from '../Mixins';
import { TeamMemberReference } from '../TeamMemberStore';
import { StdImage } from '../StdImage';

export enum PaymentTermsType {
  NET = 'net',
  NET_CASH = 'net_cash',
  PREPAID = 'prepaid',
  INVOICE_MONTH = 'invoice_month',
  CUSTOM = 'custom',
}

export enum PaymentLikelihoodType {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  ALWAYS = 'always',
}

export const formatPaymentLikelihoodType = (
  intl: IntlShape,
  type: PaymentLikelihoodType
) => {
  switch (type) {
    case PaymentLikelihoodType.ALWAYS:
      return intl.formatMessage({ defaultMessage: 'Always' });
    case PaymentLikelihoodType.MEDIUM:
      return intl.formatMessage({ defaultMessage: 'Medium' });
    case PaymentLikelihoodType.HIGH:
      return intl.formatMessage({ defaultMessage: 'High' });
    case PaymentLikelihoodType.LOW:
      return intl.formatMessage({ defaultMessage: 'Low' });
    default:
      return 'UNKOWN';
  }
};

export const PaymentLikelihoodModel = types.model('PaymentLikelihood', {
  type: types.enumeration<PaymentLikelihoodType>(
    'PaymentLikelihoodType',
    Object.values(PaymentLikelihoodType)
  ),
  updated_at: types.optional(DateTime, () => new Date()),
  updated_by: types.maybeNull(types.late(() => TeamMemberReference)),
});

export const Company = types
  .compose(
    CommomMixin,
    ResponsibleMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      timeline: types.maybeNull(types.array(TimelineLog)),
      company_number: types.number,
      name: types.string,
      logo_icon: types.maybeNull(StdImage),
      vat_number: types.maybeNull(types.string),

      phone: types.maybeNull(types.string),
      email: types.maybeNull(types.string),

      address: types.maybeNull(Address),

      domains: types.array(
        types.model({
          domain: types.string,
        })
      ),

      payment_terms_type: types.enumeration<PaymentTermsType>(
        'PaymentTermsType',
        Object.values(PaymentTermsType)
      ),
      days_of_credit: types.optional(types.number, 0),
      currency: types.optional(types.string, 'DKK'),
      payment_likelihood: types.maybeNull(PaymentLikelihoodModel),

      default_att: types.maybeNull(types.string),
      add_cc_on_invoice: types.optional(types.boolean, true),
      add_cc_on_offer: types.optional(types.boolean, true),

      invoiced_amount: types.optional(types.number, 0),
    })
  )
  .named('Company')
  .views((self) => ({
    get initials(): string {
      if (self.name.split(' ').length > 1) {
        const components = self.name.trim().split(' ');
        return (components[0][0] + components[1][0]).toUpperCase();
      }
      return self.name.slice(0, 2).toUpperCase();
    },
  }))
  .actions((self) => ({
    setTimeline(timeline: ITimelineLogSnapshotIn[]) {
      self.timeline = timeline as any;
    },
  }));

export const CompanyStore = modelWithCRUD('companies', Company)
  .named('CompanyStore')
  .actions((self) => ({
    validateVatNumber: flow(function* validateVatNumber(vatNumber: string) {
      const res = yield self.env.api
        .get(`/validate-vat/${encodeURIComponent(vatNumber)}/`)
        .then((res) => res.data);
      return res as {
        valid: boolean;
        name?: string;
        address?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        vat_number?: string;
        request_date?: string;
      };
    }),
    getTimeline: flow(function* getTimeline(company: ICompany) {
      const { data } = yield self.env.api.get<ITimelineLogSnapshotIn[]>(
        `${self.apiPath}/${company.id}/timeline/`
      );
      company.setTimeline(data);
      return company.timeline;
    }),
  }));

export interface ICompany extends Instance<typeof Company> {}
export interface ICompanySnapshotIn extends SnapshotIn<typeof Company> {}
export interface ICompanySnapshotOut extends SnapshotOut<typeof Company> {}

export interface IPaymentLikelihood
  extends Instance<typeof PaymentLikelihoodModel> {}

export const CompanyReference = types.reference(
  types.late((): IAnyModelType => Company),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).companyStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);
